import React, { createContext, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

const SheetContext = createContext(null);

export function Sheet({ children, open, defaultOpen = false, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = typeof open === "boolean";
  const currentOpen = isControlled ? open : internalOpen;

  const setOpen = (nextValue) => {
    if (!isControlled) {
      setInternalOpen(nextValue);
    }
    onOpenChange?.(nextValue);
  };

  const value = useMemo(
    () => ({
      open: currentOpen,
      setOpen,
    }),
    [currentOpen]
  );

  return <SheetContext.Provider value={value}>{children}</SheetContext.Provider>;
}

export function SheetTrigger({ render, children }) {
  const context = useSheetContext();
  const element = render || children;

  if (!React.isValidElement(element)) {
    return null;
  }

  return React.cloneElement(element, {
    onClick: (event) => {
      element.props?.onClick?.(event);
      if (!event.defaultPrevented) {
        context.setOpen(true);
      }
    },
  });
}

export function SheetClose({ render, children }) {
  const context = useSheetContext();
  const element = render || children;

  if (!React.isValidElement(element)) {
    return null;
  }

  return React.cloneElement(element, {
    onClick: (event) => {
      element.props?.onClick?.(event);
      if (!event.defaultPrevented) {
        context.setOpen(false);
      }
    },
  });
}

export function SheetContent({ className = "", children, showClose = true }) {
  const context = useSheetContext();

  if (!context.open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <>
      <button
        type="button"
        className="fixed inset-0 z-[90] bg-black/55 backdrop-blur-[1px]"
        onClick={() => context.setOpen(false)}
        aria-label="Fechar painel"
      />
      <aside
        className={cn(
          "fixed inset-y-0 right-0 z-[95] flex w-full max-w-[min(100vw,560px)] flex-col border-l border-border bg-card text-card-foreground shadow-2xl",
          className
        )}
        role="dialog"
        aria-modal="true"
      >
        {showClose ? (
          <button
            type="button"
            onClick={() => context.setOpen(false)}
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-background/80 text-muted-foreground transition hover:text-foreground"
            aria-label="Fechar painel"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
        {children}
      </aside>
    </>,
    document.body
  );
}

export function SheetHeader({ className = "", ...props }) {
  return <div className={cn("border-b border-border px-6 py-5", className)} {...props} />;
}

export function SheetTitle({ className = "", ...props }) {
  return <h2 className={cn("font-display text-lg font-semibold text-foreground", className)} {...props} />;
}

export function SheetDescription({ className = "", ...props }) {
  return <p className={cn("mt-1 text-sm text-muted-foreground", className)} {...props} />;
}

export function SheetFooter({ className = "", ...props }) {
  return <div className={cn("border-t border-border px-6 py-4", className)} {...props} />;
}

function useSheetContext() {
  const context = useContext(SheetContext);
  if (!context) {
    throw new Error("Sheet components devem ser usados dentro de <Sheet>.");
  }
  return context;
}
